import { inject, injectable } from "inversify";
import { action, computed, makeObservable, observable } from "mobx";
import { fromPromise } from "mobx-utils";
import type { IntervalOptionType } from "../types/IntervalOptionType";
import { INTERVAL_OPTIONS } from "./IntervalOptions";
import { AutoSyncCasesDataLayerSymbol } from "./AutoSyncCasesDataLayerSymbol";
import type { IAutoSyncCasesDataLayer } from "../types/IAutoSyncCasesDataLayer";

@injectable()
export class AutoSyncCasesSettingsStore {
  selectedOption: IntervalOptionType = INTERVAL_OPTIONS[0];
  isRunning = false;
  isSaving = false;
  nextRun = "";
  previousRun = "";

  constructor(@inject(AutoSyncCasesDataLayerSymbol) private autoSyncCasesDataLayer: IAutoSyncCasesDataLayer) {
    makeObservable(this, {
      selectedOption: observable.deep,
      isRunning: observable,
      isSaving: observable,
      nextRun: observable,
      previousRun: observable,
      setSelectedOption: action,
      setIsRunning: action,
      setIsSaving: action,
      runNow: action,
      saveSettings: action,
      init: computed,
    });
  }

  get init() {
    return fromPromise(this.initData());
  }

  private async initData() {
    const { nextRun, previousRun, selectedOption, caseShowRunNowJob } = await this.autoSyncCasesDataLayer.init();
    this.selectedOption = INTERVAL_OPTIONS.find((opt) => opt.value === selectedOption) ?? INTERVAL_OPTIONS[0];
    this.nextRun = nextRun;
    this.previousRun = previousRun;
    return { showRunNowJob: caseShowRunNowJob };
  }

  setSelectedOption(selectedOption: IntervalOptionType) {
    this.selectedOption = selectedOption;
  }

  setIsRunning(val: boolean) {
    this.isRunning = val;
  }

  setIsSaving(val: boolean) {
    this.isSaving = val;
  }

  async runNow() {
    this.setIsRunning(true);
    await this.autoSyncCasesDataLayer.runNow();
    this.setIsRunning(false);
  }

  async saveSettings() {
    this.setIsSaving(true);
    const res = await this.autoSyncCasesDataLayer.save(this.selectedOption);
    this.nextRun = res.nextRun ?? "";
    this.previousRun = res.previousRun ?? "";
    this.setIsSaving(false);
  }
}
